/* eslint-disable consistent-return */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
// NPM Imports
import React, { Fragment } from "react";
import ReactDOM from "react-dom";
import { connect } from "react-redux";
import uuid from "react-native-uuid";
import { Tooltip } from "@revvsales/components";
import PropTypes from "prop-types";
import { SketchPicker } from "react-color";
import { cloneDeep } from "lodash";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Components Imports
import { ContentState } from "draft-js";
import CustomTableText from "./CustomTableText";
import TableOptions from "./TableOptions";

// Action Imports
import {
  updateDocumentClickedComponentID,
  preventEditorToDeactivate,
} from "../../actions/DocumentActions/index";
import EditorActions from "../../actions/EditorActions";

// Internals Imports
import TableAddColumnRight from "../../assets/table-icons/ic-column-add.svg";
import TableAddColumnLeft from "../../assets/table-icons/ic-column-add-left.svg";
import TableRemoveColumn from "../../assets/table-icons/ic-column-remove.svg";
import TableAddRowBelow from "../../assets/table-icons/ic-row-add.svg";
import TableAddRowAbove from "../../assets/table-icons/ic-row-add-up.svg";
import TableRemoveRow from "../../assets/table-icons/ic-row-delete.svg";
import TableBorder from "../../assets/table-icons/ic-border-color.svg";
import { DOCUMENTS } from "../../constants";
import { updateCellData, getCellData } from "./utils";
import { normaliseTableData } from "../../utils/tableUtils";
import { DOCUMENT_MODE } from "../../constants/document";

// Destructuring
const { MAX_COLUMN_ALLOWED, FONT_COLORS } = DOCUMENTS;
const { nukeEditorState } = EditorActions;

class Table extends React.Component {
  tableRef = React.createRef();

  optionsDropDownRef = React.createRef();

  tableOptionsWrapperRef = React.createRef();

  stylingDropDownRef = React.createRef();

  tableOptionsColorPickerRef = React.createRef();

  state = {
    focusRowID: null,
    focusColumnID: null,
    tableData: {
      attributes: {
        hiddenColumns: this.props.hiddenCols || [],
        columnWidth: [],
      },
      data: [],
    },
    tempActiveCellID: null,
    showBackgroundColorDropdown: false,
    showBorderColorDropdown: false,
    selectedColID: null,
    selectedRowID: null,
    colorSelected: "",
  };

  componentDidMount() {
    const {
      section,
      headers,
      sectionID,
      hoveredActiveComponentId,
    } = this.props;
    this._createTableData(section, headers);
    if (sectionID === hoveredActiveComponentId) {
      this._focusCellZeroZero();
    }
  }

  componentDidUpdate(prevProps) {
    const { focusRowID, focusColumnID } = this.state;
    const {
      section,
      sectionID,
      headers,
      type,
      clickedActiveComponentId,
      tableFor,
    } = this.props;
    if (
      section.id !== prevProps.section.id ||
      headers !== prevProps.headers ||
      section.data !== prevProps.section.data
    ) {
      this._createTableData(section, headers);
    }
    if (
      tableFor === "DOCUMENT_SECTION" &&
      (focusColumnID !== null || focusRowID !== null)
    ) {
      document.addEventListener("click", this._handleClickOutside, false);
    }
    if (
      type === "gSheet" &&
      this.props.colResize &&
      (focusColumnID === null || focusRowID === null) &&
      clickedActiveComponentId === sectionID
    ) {
      this._focusCellZeroZero();
    }

    if (
      clickedActiveComponentId !== null &&
      this.state.tempActiveCellID !== clickedActiveComponentId
    ) {
      // This "tempActiveCellID" is being used to activate the table cell after resizing of table cell,
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ tempActiveCellID: clickedActiveComponentId });
    }

    if (
      clickedActiveComponentId === null &&
      this.state.tempActiveCellID !== null &&
      !this.props.isTableCellActive
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        tempActiveCellID: null,
        showBackgroundColorDropdown: false,
        showBorderColorDropdown: false,
        selectedColID: null,
        selectedRowID: null,
      });

      this._closeTableColorPicker();
    }
    if (
      this.props.tableFor === "DOCUMENT_SECTION" &&
      this.props.activeCell === null
    ) {
      document.removeEventListener("click", this._handleClickOutside, false);
    }
  }

  componentWillUnmount() {
    if (this.props.tableFor === "DOCUMENT_SECTION") {
      document.removeEventListener("click", this._handleClickOutside, false);
    }
  }

  _attachResizeElement = () => {
    const table = this.tableRef.current;
    if (table && this.state.focusColumnID !== null && this.props.colResize) {
      let currCol;
      let nextCol;
      let currColIndex = null;
      let currColWidth = null;
      let currColFixedWidth = null;
      let nextColFixedWidth = null;
      let nextColWidth = null;
      let downX = null;
      let diffX = null;
      let grip = null;
      let tableHeight = table.offsetHeight;
      const firstRow = table.rows[0].cells;
      Array.prototype.forEach.call(firstRow, td => {
        if (td.childNodes[1]) {
          td.removeChild(td.childNodes[1]);
        }
        td.style.position = "relative";
        grip = document.createElement("div");
        grip.innerHTML = "&nbsp;";
        grip.style.top = 0;
        grip.style.right = 0;
        grip.style.bottom = 0;
        grip.style.width = "5px";
        grip.style.height = `${tableHeight}px`;
        grip.style.position = "absolute";
        grip.style.cursor = "col-resize";
        grip.classList.add("grip-hover");
        grip.addEventListener("mousedown", e => {
          downX = e.pageX;
          currCol = td;
          nextCol = currCol.nextElementSibling;
          currColFixedWidth = currCol.offsetWidth;
          nextColFixedWidth = (nextCol && nextCol.offsetWidth) || 0;
          table.style.cursor = "col-resize";
          table.classList.add("disable-select");
        });
        const isGripAvailable =
          td.getElementsByClassName("grip-hover").length > 0;
        if (!isGripAvailable) td.appendChild(grip);
      });
      table.addEventListener("mousemove", e => {
        const dragMoving = e.pageX <= downX ? "left" : "right";
        let keepDragging = false;
        if (currCol) {
          currColIndex = currCol.cellIndex;
          diffX = e.pageX - downX;
          if (
            (dragMoving === "left" && currCol.offsetWidth > 30) ||
            (nextCol && dragMoving === "right" && nextCol.offsetWidth > 30)
          ) {
            keepDragging = true;
          }
          if (keepDragging) {
            tableHeight = table.offsetHeight;
            if (grip) {
              grip.style.height = `${tableHeight}px`;
            }
            currColWidth = `${currColFixedWidth + diffX}px`;
            currCol.style.width = currColWidth;
            if (nextCol) {
              nextColWidth = `${nextColFixedWidth - diffX}px`;
              nextCol.style.width = nextColWidth;
            }
            Array.prototype.forEach.call(firstRow, td => {
              if (
                td.cellIndex !== currColIndex &&
                td.cellIndex !== currColIndex + 1
              ) {
                td.style.width = this.state.tableData.attributes.columnWidth[
                  td.cellIndex
                ];
              }
            });
          }
        }
      });
      const stopDrag = () => {
        if (currCol) {
          const { tableData } = this.state;
          const attributesClone = JSON.parse(
            JSON.stringify(tableData.attributes),
          );
          attributesClone.columnWidth[currColIndex] = currColWidth;
          attributesClone.columnWidth[currColIndex + 1] = nextColWidth;
          const updatedTableData = {
            data: tableData.data,
            attributes: {
              ...attributesClone,
            },
          };
          this._saveTable(updatedTableData, this.state.tempActiveCellID);
        }
        currCol = null;
        nextCol = null;
        currColIndex = null;
        currColWidth = null;
        currColFixedWidth = null;
        nextColFixedWidth = null;
        nextColWidth = null;
        downX = null;
        diffX = null;
        grip = null;
        tableHeight = null;
        table.style.cursor = "auto";
        table.classList.remove("disable-select");
      };
      table.addEventListener("mouseup", stopDrag);
      table.addEventListener("mouseleave", stopDrag);
    } else if (table) {
      table.removeEventListener("mousemove", () => {});
      table.removeEventListener("mouseup", () => {});
      table.removeEventListener("mouseleave", () => {});
      if (this.state.focusColumnID === null) {
        Array.prototype.forEach.call(table.rows[0].cells, td => {
          if (td.childNodes[1]) {
            td.removeChild(td.childNodes[1]);
          }
        });
      }
    }
  };

  _focusCellZeroZero = () => {
    this.setState({ focusRowID: 0, focusColumnID: 0 });
  };

  _resetFocus = () => {
    this.setState({ focusRowID: null, focusColumnID: null });
  };

  _createTableData = (section, headers) => {
    let rows = section.data || [];
    if (headers && Array.isArray(headers) && headers.length > 0) {
      rows = [{ attributes: { header: true }, data: headers }].concat(rows);
    }
    const table = normaliseTableData(
      rows,
      this.props.type,
      this.props.hiddenCols,
    );
    this.setState({ tableData: table });
  };

  _emptyCell = () => {
    const cellData = {
      data: {
        data: ContentState.createFromText(""),
      },
      attributes: {
        id: uuid.v4(),
      },
    };
    if (this.props.mode === DOCUMENT_MODE.DOCUMENT)
      cellData.data.currentContent = ContentState.createFromText("");
    return cellData;
  };

  _addRow = (direction, event) => {
    event.preventDefault();
    event.stopPropagation();
    this.props.nukeEditorState();
    const {
      focusRowID,
      focusColumnID,
      tableData: { data, attributes },
    } = this.state;

    const { data: rowData, attributes: rowAttributes } = data[focusRowID];
    const newRowData = rowData.map(() => this._emptyCell());
    const newRow = {
      data: newRowData,
      attributes: {
        ...rowAttributes,
        id: uuid.v4(),
      },
    };

    let newData;
    if (direction === "U") {
      // Add row above
      newData = data
        .slice(0, focusRowID)
        .concat(newRow, data.slice(focusRowID, data.length));
    } else if (direction === "D") {
      // Add row below
      newData = data
        .slice(0, focusRowID + 1)
        .concat(newRow, data.slice(focusRowID + 1, data.length));
    }
    attributes.rowCount += 1;
    const tableData = { data: newData, attributes };
    // This "activeCellID" is for clickedActiveComponentId
    let activeCellID = null;
    if (newRowData && newRowData.length > 0) {
      activeCellID =
        newRowData[focusColumnID] && newRowData[focusColumnID].attributes
          ? newRowData[focusColumnID].attributes.id
          : null;
    }
    this._saveTable(tableData, activeCellID);
    this.setState({
      tableData,
      focusRowID: direction === "D" ? focusRowID + 1 : focusRowID,
      focusColumnID,
      showBackgroundColorDropdown: false,
      showBorderColorDropdown: false,
    });
    this._closeTableColorPicker();
  };

  _removeRow = event => {
    event.preventDefault();
    event.stopPropagation();
    this.props.nukeEditorState();
    const {
      focusRowID,
      focusColumnID,
      tableData: { data, attributes },
    } = this.state;
    if (Array.isArray(data) && data.length > 1) {
      const newData = data
        .slice(0, focusRowID)
        .concat(data.slice(focusRowID + 1, data.length));
      attributes.rowCount -= 1;
      const tableData = { data: newData, attributes };

      // This "activeCellID" is for clickedActiveComponentId
      let activeCellID = null;
      let activeRowIndex = focusRowID;
      if (focusRowID === newData.length) {
        activeRowIndex = focusRowID - 1;
      }
      if (
        newData[activeRowIndex] &&
        newData[activeRowIndex].data &&
        newData[activeRowIndex].data.length > 0
      ) {
        const activeRow = newData[activeRowIndex].data;
        activeCellID =
          activeRow[focusColumnID] && activeRow[focusColumnID].attributes
            ? activeRow[focusColumnID].attributes.id
            : null;
      }
      this._saveTable(tableData, activeCellID);
      this.setState({
        tableData,
        focusRowID: focusRowID === newData.length ? focusRowID - 1 : focusRowID,
        focusColumnID,
        showBackgroundColorDropdown: false,
        showBorderColorDropdown: false,
      });
      this._closeTableColorPicker();
    }
  };

  _addColumn = (direction, event) => {
    event.preventDefault();
    event.stopPropagation();
    this.props.nukeEditorState();
    const {
      focusRowID,
      focusColumnID,
      tableData: { data, attributes },
    } = this.state;

    // let addedColumn = null;
    const newData = data.map(row => {
      const { data: rData } = row;
      if (direction === "R") {
        // Add col on right
        row.data = [
          ...rData.slice(0, focusColumnID + 1),
          this._emptyCell(),
          ...rData.slice(focusColumnID + 1),
        ];
      } else if (direction === "L") {
        // Add col on left
        row.data = [
          ...rData.slice(0, focusColumnID),
          this._emptyCell(),
          ...rData.slice(focusColumnID),
        ];
      }
      return row;
    });
    attributes.columnCount += 1;
    const tableData = { data: newData, attributes };

    // This "activeCellID" is for clickedActiveComponentId
    let activeCellID = null;
    if (
      newData[focusRowID] &&
      newData[focusRowID].data &&
      newData[focusRowID].data.length > 0
    ) {
      const activeRow = newData[focusRowID].data;
      const activeColumnIndex =
        direction === "R" ? focusColumnID + 1 : focusColumnID;
      activeCellID =
        activeRow[activeColumnIndex] && activeRow[activeColumnIndex].attributes
          ? activeRow[activeColumnIndex].attributes.id
          : null;
    }
    this._saveTable(tableData, activeCellID);
    this.setState({
      tableData,
      focusColumnID: direction === "R" ? focusColumnID + 1 : focusColumnID,
      focusRowID,
      showBackgroundColorDropdown: false,
      showBorderColorDropdown: false,
    });
    this._closeTableColorPicker();
  };

  _removeColumn = event => {
    event.preventDefault();
    event.stopPropagation();
    this.props.nukeEditorState();
    const {
      focusRowID,
      focusColumnID,
      tableData: { data, attributes },
    } = this.state;

    const newData = data.map(row => {
      const newRowData = row.data
        .slice(0, focusColumnID)
        .concat(row.data.slice(focusColumnID + 1, row.data.length));
      return { ...row, data: newRowData };
    });
    attributes.columnCount -= 1;
    const tableData = { data: newData, attributes };

    // This "activeCellID" is for clickedActiveComponentId
    let activeCellID = null;
    if (
      newData[focusRowID] &&
      newData[focusRowID].data &&
      newData[focusRowID].data.length > 0
    ) {
      let activeColumnIndex = focusColumnID;
      if (focusColumnID === newData[focusRowID].data.length) {
        activeColumnIndex = focusColumnID - 1;
      }
      const activeRow = newData[focusRowID].data;
      activeCellID =
        activeRow[activeColumnIndex] && activeRow[activeColumnIndex].attributes
          ? activeRow[activeColumnIndex].attributes.id
          : null;
    }
    this._saveTable(tableData, activeCellID);
    this.setState({
      tableData,
      focusRowID,
      focusColumnID:
        focusColumnID === attributes.columnCount
          ? focusColumnID - 1
          : focusColumnID,
    });
    this.setState({
      tableData,
      showBackgroundColorDropdown: false,
      showBorderColorDropdown: false,
    });
    this._closeTableColorPicker();
  };

  _updateFocusID = (event, rowID, columnID, cellID) => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    const { focusColumnID, focusRowID } = this.state;
    const {
      updateHoveredSectionId,
      section,
      isHoverdComponentActive,
      activeCell,
    } = this.props;
    if (updateHoveredSectionId && !isHoverdComponentActive)
      updateHoveredSectionId(section ? section.id : null);

    if (!(rowID === focusRowID && columnID === focusColumnID)) {
      this.setState({
        focusRowID: rowID,
        focusColumnID: columnID,
      });
    }
    if (
      !(
        Array.isArray(activeCell) &&
        activeCell[0] === rowID &&
        activeCell[1] === columnID
      )
    ) {
      if (!this.props.preventClickedComponentIdUpdate) {
        this.props.nukeEditorState();
      }

      this.props.updateDocumentClickedComponentID(
        cellID,
        !!this.props.preventClickedComponentIdUpdate,
        section ? section.type : null,
      );
    }

    if (this.props.createDocSectionRef) {
      this.props.createDocSectionRef(React.createRef());
    }
  };

  _applyBackgroundColor = event => {
    const { tableData } = this.state;
    const { activeCell } = this.props;

    const clonedTableData = cloneDeep(tableData);

    const { data: clonedData, attributes } = clonedTableData;

    let change = false;

    if (activeCell !== null) {
      change =
        clonedData[activeCell[0]].data[activeCell[1]].attributes.bgColor !==
        event.hex;
      clonedData[activeCell[0]].data[activeCell[1]].attributes.bgColor =
        event.hex;
    } else if (this.state.selectedColID !== null) {
      clonedData.forEach(row => {
        change =
          row.data[this.state.selectedColID].attributes.bgColor !== event.hex;
        row.data[this.state.selectedColID].attributes.bgColor = event.hex;
      });
    } else if (this.state.selectedRowID !== null) {
      clonedData[this.state.selectedRowID].data.forEach(dt => {
        change = dt.attributes.bgColor !== event.hex;
        dt.attributes.bgColor = event.hex;
      });
    }

    this.setState({
      colorSelected: event.hex,
    });

    const updatedTableData = {
      data: [...clonedData],
      attributes: {
        ...attributes,
      },
    };

    if (change)
      this._saveTableData(
        updatedTableData,
        this.state.focusRowID,
        this.state.focusColumnID,
        change,
      );

    this.props.preventEditorToDeactivate(false);
  };

  _applyBorderColor = event => {
    const { tableData } = this.state;
    const attributesClone = cloneDeep(tableData.attributes);
    let change = false;
    change = attributesClone.borderColor !== event.hex;
    attributesClone.borderColor = event.hex;
    const updatedTableData = {
      data: tableData.data,
      attributes: {
        ...attributesClone,
      },
    };

    this.setState({
      colorSelected: event.hex,
    });

    this._saveTableData(
      updatedTableData,
      this.state.focusRowID,
      this.state.focusColumnID,
      change,
    );

    this.props.preventEditorToDeactivate(false);
  };

  _toggleDropdown = (e, type) => {
    e.preventDefault();

    if (type === "BackgroundColor") {
      if (this.state.showBackgroundColorDropdown) {
        this._closeTableColorPicker();
      } else this.props.openTableColorpicker();

      this.setState(prevstate => ({
        showBackgroundColorDropdown: !prevstate.showBackgroundColorDropdown,
        showBorderColorDropdown: false,
      }));
    } else if (type === "BorderColor") {
      if (this.state.showBorderColorDropdown) {
        this._closeTableColorPicker();
      } else this.props.openTableColorpicker();

      this.setState(prevstate => ({
        showBorderColorDropdown: !prevstate.showBorderColorDropdown,
        showBackgroundColorDropdown: false,
      }));
    }
    this.props.preventEditorToDeactivate(false);
  };

  _handleDropdownsOnBlur = () => {
    this.setState({
      selectedRowID: null,
      selectedColID: null,
    });
  };

  _handleColorPicker = () => {
    this.setState({
      showBackgroundColorDropdown: false,
      showBorderColorDropdown: false,
    });
  };

  _showStylingOptions = () => {
    if (this.props.type === "gSheet") {
      return null;
    }
    const { focusRowID, focusColumnID, tableData } = this.state;
    const { readOnly, isTableCellActive } = this.props;
    // check if there is any focus column or not
    if (
      (focusColumnID === undefined ||
        focusColumnID === null ||
        focusRowID === undefined ||
        focusRowID === null ||
        !tableData) &&
      !isTableCellActive
    ) {
      return null;
    }
    const { data } = tableData;
    if (
      (data[focusRowID] === null || data[focusRowID] === undefined) &&
      !isTableCellActive
    ) {
      return null;
    }

    return !readOnly ? (
      <div
        className="table-add-column styling-icons"
        ref={this.props.tableColorpickerDropdownRef}>
        <Fragment>
          <div ref={this.stylingDropDownRef} className="table-style-items">
            <Tooltip
              placement="top"
              overlay="Background Color"
              align={{
                offset: [0, 3],
              }}>
              <span
                data-test="table-background-color-span"
                className="btn-positive"
                onMouseDown={e => {
                  this._toggleDropdown(e, "BackgroundColor");
                }}
                role="button"
                tabIndex={0}>
                <i>
                  <FontAwesomeIcon icon={["far", "fill-drip"]} />
                </i>
              </span>
            </Tooltip>

            <Tooltip
              placement="top"
              overlay="Border Color"
              align={{
                offset: [0, 3],
              }}>
              <span
                data-test="table-border-color-span"
                className="btn-positive"
                onMouseDown={e => {
                  this._toggleDropdown(e, "BorderColor");
                }}
                role="button"
                tabIndex={0}>
                <img className="" src={TableBorder} alt="Border Color" />
              </span>
            </Tooltip>
          </div>
          {(this.state.showBackgroundColorDropdown ||
            this.state.showBorderColorDropdown) && (
            <div className="table-customcolor-dropdown-wrapper">
              <SketchPicker
                color={this.state.colorSelected}
                onChange={event => {
                  if (this.state.showBackgroundColorDropdown) {
                    this._applyBackgroundColor(event);
                  }
                  if (this.state.showBorderColorDropdown) {
                    this._applyBorderColor(event);
                  }
                }}
                presetColors={FONT_COLORS}
                width={225}
                disableAlpha
                data-test="table-color-option-picker"
              />
            </div>
          )}
        </Fragment>
      </div>
    ) : null;
  };

  _showColumnOptions = () => {
    if (this.props.type === "gSheet") {
      return null;
    }
    const { focusRowID, focusColumnID, tableData } = this.state;
    const { readOnly } = this.props;
    // check if there is any focus column or not
    if (
      focusColumnID === undefined ||
      focusColumnID === null ||
      focusRowID === undefined ||
      focusRowID === null ||
      !tableData
    ) {
      return null;
    }
    const { data } = tableData;
    if (data[focusRowID] === null || data[focusRowID] === undefined) {
      return null;
    }
    const { data: rowData } = data[focusRowID];

    const showAddColumn = rowData.length < MAX_COLUMN_ALLOWED;
    const showRemoveColumn = rowData.length > 1;
    return (showAddColumn || showRemoveColumn) && !readOnly ? (
      <div className="table-add-column">
        <Fragment>
          {showAddColumn && (
            <div
              className={`table-add-column-items ${
                this.state.selectedColID !== null ? "disabled-options" : ""
              }`}>
              <Tooltip
                placement="top"
                overlay="Add Column Left"
                align={{
                  offset: [0, 3],
                }}>
                <span
                  data-test="table-add-column-left-button-span"
                  className="btn-positive"
                  onMouseDown={e => this._addColumn("L", e)}
                  // onKeyDown={e => this._addColumn("L", e)}
                  role="button"
                  tabIndex={0}>
                  <img className="" src={TableAddColumnLeft} alt="Add Column" />
                </span>
              </Tooltip>
              <Tooltip
                placement="top"
                overlay="Add Column Right"
                align={{
                  offset: [0, 3],
                }}>
                <span
                  data-test="table-add-column-right-button-span"
                  className="btn-positive"
                  onMouseDown={e => this._addColumn("R", e)}
                  // onKeyDown={e => this._addColumn("R", e)}
                  role="button"
                  tabIndex={0}>
                  <img
                    className=""
                    src={TableAddColumnRight}
                    alt="Add Column"
                  />
                </span>
              </Tooltip>
            </div>
          )}
          {showRemoveColumn && (
            <Tooltip
              placement="top"
              overlay="Remove Column"
              align={{
                offset: [0, 3],
              }}>
              <span
                data-test="table-remove-column-button"
                className="btn-negative"
                onMouseDown={this._removeColumn}
                // onKeyDown={this._removeColumn}
                role="button"
                tabIndex={0}>
                <img className="" src={TableRemoveColumn} alt="Remove Column" />
              </span>
            </Tooltip>
          )}
        </Fragment>
      </div>
    ) : null;
  };

  _showRowOptions = () => {
    if (this.props.type === "gSheet") {
      return null;
    }
    const { focusRowID, tableData } = this.state;
    const { readOnly } = this.props;
    // check if there is any focus row or not
    if (focusRowID === undefined || focusRowID === null || !tableData) {
      return null;
    }
    const { data } = tableData;
    if (data[focusRowID] === null || data[focusRowID] === undefined) {
      return null;
    }
    const { attributes: rowAttributes } = data[focusRowID];

    // this is required to allow row specific options.
    // e.g for merged group view of deal table to have remove group
    if (rowAttributes.rowOptions && Array.isArray(rowAttributes.rowOptions)) {
      return (
        <div className="row-options">
          {rowAttributes.rowOptions.map(option => {
            const { key, label } = option;
            const func = rowAttributes[key];
            return (
              <span
                className="revv-btn-secondary-sm"
                onMouseDown={() => func(rowAttributes.id)}
                // onKeyDown={() => func(rowAttributes.id)}
                role="button"
                tabIndex={0}>
                {label}
              </span>
            );
          })}
        </div>
      );
    }

    const showAddRow = true;
    const showRemoveRow = Array.isArray(data) && data.length > 1;
    return (showAddRow || showRemoveRow) && !readOnly ? (
      <div className="row-options">
        {showAddRow && (
          <div
            className={`table-add-column-items ${
              this.state.selectedRowID !== null ? "disabled-options" : ""
            }`}>
            <Tooltip
              placement="top"
              overlay="Add Row Above"
              align={{
                offset: [0, 3],
              }}>
              <span
                data-test="table-add-row-above-button-span"
                className="btn-positive"
                onMouseDown={e => this._addRow("U", e)}
                // onKeyDown={() => {}}
                role="button"
                tabIndex={0}>
                <img className="" src={TableAddRowAbove} alt="Add Column" />
              </span>
            </Tooltip>
            <Tooltip
              placement="top"
              overlay="Add Row Below"
              align={{
                offset: [0, 3],
              }}>
              <span
                data-test="table-add-row-below-button-span"
                className="btn-positive"
                onMouseDown={e => this._addRow("D", e)}
                // onKeyDown={() => {}}
                role="button"
                tabIndex={0}>
                <img className="" src={TableAddRowBelow} alt="Add Column" />
              </span>
            </Tooltip>
          </div>
        )}
        {showRemoveRow && (
          <Tooltip
            placement="top"
            overlay="Remove Row"
            align={{
              offset: [0, 3],
            }}>
            <span
              data-test="table-remove-row-button-span"
              className="btn-negative"
              onMouseDown={this._removeRow}
              // onKeyDown={this._removeRow}
              role="button"
              tabIndex={0}>
              <img className="" src={TableRemoveRow} alt="Add Column" />
            </span>
          </Tooltip>
        )}
      </div>
    ) : null;
  };

  _getEnableAddRowColConfigs = () => {
    const { focusRowID, tableData } = this.state;
    if (focusRowID === null) {
      return {
        enableAddRow: false,
        enableRemoveRow: false,
        enableAddColumn: false,
        enableRemoveColumn: false,
      };
    }
    const { readOnly } = this.props;
    const { data } = tableData;
    const enableAddRow = !readOnly;
    const enableRemoveRow = Array.isArray(data) && data.length > 1 && !readOnly;
    const { data: rowData } = data[focusRowID];
    const enableAddColumn = rowData.length < MAX_COLUMN_ALLOWED && !readOnly;
    const enableRemoveColumn = rowData.length > 1 && !readOnly;
    return {
      enableAddRow,
      enableRemoveRow,
      enableAddColumn,
      enableRemoveColumn,
    };
  };

  _saveTableData = (tableData, rowID, colID, change) => {
    const { section, handleBlur, readOnly } = this.props;

    let editable = false;
    if (rowID !== null && colID !== null) {
      const cellData = getCellData(tableData, rowID, colID);
      if (cellData) {
        const { attributes } = cellData;
        editable = editable || attributes.editable;
      }
    }

    if (!readOnly || editable) {
      const newSection = {
        ...section,
        data: tableData,
      };
      handleBlur(newSection, change, undefined, section);
    }
  };

  _saveTable = (tableData, activeCellID) => {
    // const { tableData } = this.state;
    const { section, readOnly, handleBlur } = this.props;
    if (!readOnly) {
      const newSection = {
        ...section,
        data: tableData,
      };
      handleBlur(newSection, undefined, undefined, section);
      if (activeCellID) {
        this.props.updateDocumentClickedComponentID(
          activeCellID,
          true,
          section ? section.type : null,
        );
      }
    }
  };

  _handleClickOutside = event => {
    if (this.props.tableFor === "DOCUMENT_SECTION") {
      // eslint-disable-next-line react/no-find-dom-node
      const domNode = ReactDOM.findDOMNode(this);
      if (!domNode || !domNode.contains(event.target)) {
        const sectionActionBtnsNode =
          this.props.sectionActionBtnsRef &&
          this.props.sectionActionBtnsRef.current;
        const customToolbarNode =
          this.props.customToolbarRef && this.props.customToolbarRef.current;
        const stylingDropDownNode =
          this.stylingDropDownRef && this.stylingDropDownRef.current;
        if (
          (sectionActionBtnsNode &&
            sectionActionBtnsNode.contains(event.target)) ||
          (customToolbarNode && customToolbarNode.contains(event.target)) ||
          (stylingDropDownNode && stylingDropDownNode.contains(event.target))
        ) {
          return;
        }
        event.preventDefault();
        this.setState({ focusRowID: null, focusColumnID: null });
        document.removeEventListener("click", this._handleClickOutside, true);
        this.props.updateDocumentClickedComponentID(null, true);
        this.props.disablePlusBtnHandler(false);
        if (this.props.updateHoveredSectionId)
          this.props.updateHoveredSectionId(null);
      }
    }
  };

  _getCellMetadata = cellData => {
    let cellValue = "";
    let colspan = "1";
    let style = {};
    let type = "text";
    let editable = false;
    let cellID;
    if (cellData && cellData instanceof Object) {
      cellValue = cellData.data;
      colspan = cellData.attributes.colspan || colspan;
      style = cellData.attributes.style || style;
      type = cellData.attributes.type || type;
      editable = Boolean(cellData.attributes.editable);
      cellID = cellData.attributes.id;
    }
    if (this.props.type === "gSheet") {
      editable = false;
    }
    return {
      attributes: {
        type,
        style,
        colspan,
        editable,
        id: cellID,
      },
      data: cellValue,
    };
  };

  _getCellView = (rowID, columnID, type, data, editable, cellID) => {
    const {
      mode,
      addNote,
      templateType,
      section,
      clickedActiveComponentId,
      removeHighlightSelection,
      showHyperlinkBox,
      closeHyperlinkBox,
      getEditorState,
      updateHoveredSectionId,
      isHoverdComponentActive,
    } = this.props;
    const { focusRowID, focusColumnID } = this.state;

    const componentMeta = {
      ...this.props.componentMeta,
      tableId: this.props.section.id,
      tableRowId: rowID,
      tableColId: columnID,
      tableCellId: cellID,
    };

    const updateCellValue = (value, change) => {
      this._updateCellValue(rowID, columnID, value, change);
    };
    const activeEditor = rowID === focusRowID && columnID === focusColumnID;

    if (type === "text") {
      return (
        <CustomTableText
          activeEditor={activeEditor}
          section={section}
          componentMeta={componentMeta}
          value={data}
          cellID={cellID}
          mode={mode}
          templateType={templateType}
          updateCellData={updateCellValue}
          editable={editable}
          removeHighlightSelection={removeHighlightSelection}
          className={`auto-size-textarea ${
            editable ? "" : "textarea-disabled"
          }`}
          addNote={addNote}
          clickedActiveComponentId={clickedActiveComponentId}
          isHoverdComponentActive={isHoverdComponentActive}
          showHyperlinkBox={showHyperlinkBox}
          closeHyperlinkBox={closeHyperlinkBox}
          getEditorState={getEditorState}
          updateHoveredSectionId={updateHoveredSectionId}
          createDocSectionRef={this.props.createDocSectionRef}
          isLocked={this.props.isLocked}
          handleDropdownsOnBlur={this._handleDropdownsOnBlur}
          handleColorPicker={this._handleColorPicker}
        />
      );
    }
    if (type === "checkbox") {
      return (
        <input
          data-test="table-checkbox-input"
          type="checkbox"
          onChange={
            editable ? e => updateCellValue(e.target.checked) : () => {}
          }
          checked={data}
        />
      );
    }
    return null;
  };

  _getCellStyle = style => {
    if (typeof style !== "string") return style;
    const styleList = style.split(";").filter(s => s !== "");
    const cellStyle = {};
    styleList.forEach(s => {
      const styleSplit = s.split(":");
      const styleType = styleSplit[0]
        .split("-")
        .map((styleString, index) => {
          if (index === 0) return styleString;
          return (
            styleString.substr(0, 1).toUpperCase() +
            styleString.substr(1).toLowerCase()
          );
        })
        .join("");
      // eslint-disable-next-line prefer-destructuring
      cellStyle[styleType] = styleSplit[1];
    });
    return cellStyle;
  };

  _getClassName = (type, spanID) => {
    const { activeCell } = this.props;

    if (!(this.props.isTableEditable && this.props.isTableCellActive)) {
      return "invisible-selector";
    }

    if (type === "ROW") {
      if (
        (Array.isArray(activeCell) && activeCell[0] === spanID) ||
        this.state.selectedRowID === spanID
      ) {
        return "left-selection";
      }
      return "inactive-left-selection";
    }

    if (type === "COLUMN") {
      if (
        (Array.isArray(activeCell) && activeCell[1] === spanID) ||
        this.state.selectedColID === spanID
      ) {
        return "top-selection";
      }
      return "inactive-top-selection";
    }
  };

  _selectRowCol = (event, type, spanID) => {
    // event.preventDefault();
    // event.stopPropagation();
    if (type === "ROW") {
      this.setState({
        selectedRowID: spanID,
        selectedColID: null,
      });
    }
    if (type === "COLUMN") {
      this.setState({
        selectedRowID: null,
        selectedColID: spanID,
      });
    }

    this.props.updateDocumentClickedComponentID(
      this.props.section.id,
      true,
      this.props.section.type,
    );
  };

  _renderCell = (rowID, columnID, cellData, header, uniqueRowID) => {
    const { readOnly, clickedActiveComponentId } = this.props;
    const {
      tableData: {
        attributes: { columnWidth = [], borderColor },
      },
    } = this.state;

    const {
      attributes: { bgColor },
    } = cellData;
    // get cell data and attributes
    const cellMetaData = this._getCellMetadata(cellData);
    const {
      attributes: { style, colspan, type, editable, id },
      data,
    } = cellMetaData;

    let editableCell = editable || !readOnly;
    if (this.props.type === "gSheet") {
      editableCell = false;
    }
    const cellView = this._getCellView(
      rowID,
      columnID,
      type,
      data,
      editableCell,
      id,
    );

    const cellStyle = this._getCellStyle(style);

    const columnSelection = (
      <span
        className={this._getClassName("COLUMN", columnID)}
        role="button"
        tabIndex={0}
        onMouseDown={e => {
          setTimeout(() => {
            this._selectRowCol(e, "COLUMN", columnID);
          }, 150);
        }}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onKeyDown={() => {}}>
        <FontAwesomeIcon icon={["far", "ellipsis-h"]} />
      </span>
    );

    const rowSelection = (
      <span
        className={this._getClassName("ROW", rowID)}
        role="button"
        tabIndex={0}
        onMouseDown={e => {
          setTimeout(() => {
            this._selectRowCol(e, "ROW", rowID);
          }, 150);
        }}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onKeyDown={() => {}}>
        <FontAwesomeIcon icon={["far", "ellipsis-v"]} />
      </span>
    );

    // apply background color
    if (bgColor) {
      cellStyle.background = bgColor;
    }

    // apply border color
    if (borderColor) {
      cellStyle.borderColor = borderColor;
    }

    // apply column with to the cell
    if (
      (header || (!header && rowID === 0)) &&
      Array.isArray(columnWidth) &&
      columnWidth[columnID]
    ) {
      cellStyle.width = columnWidth[columnID];
    }

    // check if its a header cell or not
    if (header) {
      // responsible for representing first row as header => true in g-sheet
      if (this.props.type === "gSheet" && this.props.section.firstRowAsHeader) {
        cellStyle.background = "#cfd7df";
      }
      return (
        <th
          key={`cell-${id}-${uniqueRowID}`}
          style={cellStyle}
          colSpan={colspan}
          data-rowid={rowID}
          data-columnid={columnID}
          data-id={id}>
          {cellView}
        </th>
      );
    }

    if (!header && rowID === 0 && this.props.type === "gSheet") {
      // responsible for representing first row as header => false in g-sheet
      if (
        this.props.type === "gSheet" &&
        this.props.section.firstRowAsHeader === false
      ) {
        cellStyle.background = "white";
      }

      return (
        <th
          key={`cell-${uniqueRowID}-${id}`}
          style={cellStyle}
          colSpan={colspan}
          className={`table-cell ${
            clickedActiveComponentId === (cellData && cellData.attributes.id)
              ? "isactivecell"
              : ""
          } ${
            this.state.selectedRowID === rowID ||
            this.state.selectedColID === columnID
              ? "row-selected"
              : ""
          }`}
          data-rowid={rowID}
          data-columnid={columnID}
          data-id={id}>
          {rowID === 0 ? columnSelection : null}
          {columnID === 0 ? rowSelection : null}
          {cellView}
        </th>
      );
    }
    return (
      <td
        key={`cell-${uniqueRowID}-${id}`}
        style={cellStyle}
        colSpan={colspan}
        className={`table-cell ${
          this.state.selectedRowID === rowID ||
          this.state.selectedColID === columnID
            ? "row-selected"
            : ""
        } ${
          clickedActiveComponentId === (cellData && cellData.attributes.id)
            ? "isactivecell"
            : ""
        }`}
        data-rowid={rowID}
        data-columnid={columnID}
        data-id={id}>
        <div>
          {columnID === 0 && rowID === 0 && (
            <span className="parent-span">
              {columnSelection}
              {rowSelection}
            </span>
          )}
          {columnID > 0 && rowID === 0 && columnSelection}
          {columnID === 0 && rowID > 0 && rowSelection}
          {cellView}
        </div>
      </td>
    );
  };

  _tableClick = e => {
    const clickFunction = cell => {
      const { rowid, columnid, id } = cell.dataset;
      if (
        this.state.tableData &&
        this.state.tableData.data &&
        this.state.tableData.data.length > 0
      ) {
        const { data: dataArray } = this.state.tableData;
        const cellData = dataArray[rowid].data[columnid];
        const cellMetaData = this._getCellMetadata(cellData);
        const {
          attributes: { editable },
        } = cellMetaData;
        let editableCell = editable || !this.props.readOnly;
        if (this.props.type === "gSheet") {
          editableCell = false;
        }
        if (editableCell) {
          this._updateFocusID(
            e,
            parseInt(rowid, 10),
            parseInt(columnid, 10),
            id,
          );
        }
      }
    };

    const targetCell = e.target.closest("td") || e.target.closest("th");
    if (targetCell) {
      clickFunction(targetCell);
      this.setState({
        selectedRowID: null,
        selectedColID: null,
        showBackgroundColorDropdown: false,
        showBorderColorDropdown: false,
      });
      this._closeTableColorPicker();
    } else {
      return null;
    }
  };

  _renderTable = () => {
    const { data, attributes } = this.state.tableData;
    const { hiddenCols } = attributes;
    const hdnCols = hiddenCols || [];
    let header = false;
    let bodyView = [];
    const tableView = [];
    if (Array.isArray(data) && data.length > 0) {
      data.forEach((row, rowID) => {
        const { data: rowData, attributes: rowAttributes } = row;
        const rowView = (
          <tr key={rowAttributes.id}>
            {rowData.map((cell, columnID) => {
              const hideCol = hdnCols.includes(columnID);
              return hideCol
                ? null
                : this._renderCell(
                    rowID,
                    columnID,
                    cell,
                    Boolean(rowAttributes.header),
                    rowAttributes.id,
                  );
            })}
          </tr>
        );
        if (Boolean(rowAttributes.header) === header) {
          bodyView.push(rowView);
        } else {
          if (bodyView.length > 0) {
            if (header) {
              tableView.push(<thead>{bodyView}</thead>);
            } else {
              tableView.push(<tbody>{bodyView}</tbody>);
            }
          }
          // eslint-disable-next-line prefer-destructuring
          header = rowAttributes.header;
          bodyView = [rowView];
        }
      });
    }

    if (bodyView.length > 0) {
      if (header) {
        tableView.push(<thead>{bodyView}</thead>);
      } else {
        tableView.push(<tbody>{bodyView}</tbody>);
      }
    }
    return Array.isArray(tableView) && tableView.length > 0 ? (
      <table
        className={`component-doc-table ${this.props.className}`}
        ref={this.tableRef}
        onClick={e => this._tableClick(e)}
        onKeyPress={() => {}}>
        {tableView}
      </table>
    ) : null;
  };

  _updateCellValue = (rowID, columnID, cellValue, change) => {
    const { tableData } = this.state;
    const updatedTableData = updateCellData(
      { ...tableData },
      rowID,
      columnID,
      cellValue,
    );
    const tableOptionsColorPickerNode = this.tableOptionsColorPickerRef
      ? this.tableOptionsColorPickerRef.current
      : null;
    if (
      this.state.showBackgroundColorDropdown ||
      (tableOptionsColorPickerNode &&
        tableOptionsColorPickerNode.id === "table-options-color-picker")
    ) {
      return;
    }
    this._saveTableData(updatedTableData, rowID, columnID, change);
    this.setState({ tableData: updatedTableData });
  };

  // This "_handleTableWrapperOnClick" method is being used here for locked table only
  _handleTableWrapperOnClick = event => {
    if (this.props.tableFor === "DOCUMENT_SECTION") {
      const { clickedActiveComponentId, isLocked, section } = this.props;
      if (
        !isLocked &&
        section &&
        section.lock &&
        clickedActiveComponentId !== section.id
      ) {
        event.stopPropagation();
        this.props.updateDocumentClickedComponentID(
          section.id,
          true,
          section.type,
        );
      }
    }
  };

  // This "_handleOnBlur" method is being used here for locked table only
  _handleOnBlur = () => {
    if (this.props.tableFor === "DOCUMENT_SECTION") {
      if (
        this.props.clickedActiveComponentId === this.props.section.id &&
        this.props.section.lock &&
        this.props.preventEditorFocusOnDrag
      ) {
        this.props.updateDocumentClickedComponentID(null, true, null);
      }
    }
  };

  _decideTableOptions = () => {
    if (
      this.props.tableFor === "DOCUMENT_SECTION" &&
      (this.props.activeCell !== null || this.props.isTableCellActive) &&
      this.props.preventEditorFocusOnDrag
    ) {
      if (this.props.totalRowItems < 2) {
        return (
          <div
            ref={this.tableOptionsWrapperRef}
            className="table-options-wrapper">
            {this._showStylingOptions()}
            {this._showColumnOptions()}
            {this._showRowOptions()}
          </div>
        );
      }
      const tableOptConfig = this._getEnableAddRowColConfigs();
      if (
        tableOptConfig.enableAddRow ||
        tableOptConfig.enableRemoveRow ||
        tableOptConfig.enableAddColumn ||
        tableOptConfig.enableRemoveColumn
      ) {
        return (
          <TableOptions
            mode={this.props.mode}
            section={this.props.section}
            activeCell={this.props.activeCell}
            optionRef={this.optionsDropDownRef}
            enableAddRow={tableOptConfig.enableAddRow}
            enableRemoveRow={tableOptConfig.enableRemoveRow}
            enableAddColumn={tableOptConfig.enableAddColumn}
            enableRemoveColumn={tableOptConfig.enableRemoveColumn}
            addRow={this._addRow}
            removeRow={this._removeRow}
            addColumn={this._addColumn}
            removeColumn={this._removeColumn}
            disablePlusBtnHandler={this.props.disablePlusBtnHandler}
            applyBackgroundColor={this._applyBackgroundColor}
            applyBorderColor={this._applyBorderColor}
            tableOptionsColorPickerRef={this.tableOptionsColorPickerRef}
          />
        );
      }
    }
    return null;
  };

  _closeTableColorPicker = () => {
    if (this.props.tableFor === "DOCUMENT_SECTION")
      this.props.closeTableColorpicker();
  };

  render() {
    const tbodyMarkup = this._renderTable();
    if (this.props.tableFor === "DOCUMENT_SECTION") {
      if (this.props.section && !this.props.section.lock) {
        this._attachResizeElement();
      }
    } else if (this.props.type === "gSheet" && this.props.section) {
      this._attachResizeElement();
    }
    return tbodyMarkup ? (
      <div
        id={this.props.section.id}
        className={`table-component ${
          this.props.activeCell !== null ? "active-table" : ""
        } ${this.props.section.lock ? "locked-table" : ""}`}
        onClick={this._handleTableWrapperOnClick}
        onKeyPress={() => {}}
        onBlur={this._handleOnBlur}
        role="button"
        tabIndex={0}>
        <div className="div-table-container">
          {this._decideTableOptions()}
          {tbodyMarkup}
        </div>
      </div>
    ) : (
      <b>Loading Table . . . </b>
    );
  }
}

const mapStateToProps = (state, props) => {
  const {
    section: { data: tableData },
    tableFor,
  } = props;
  const {
    clickedActiveComponentId,
    preventClickedComponentIdUpdate,
    preventEditorFocusOnDrag,
  } = state.DocumentStore;
  let activeCell = null;
  if (tableData && Array.isArray(tableData.data) && tableData.data.length > 0) {
    tableData.data.forEach((row, rowIndex) => {
      const { data: rowData } = row;
      if (Array.isArray(rowData)) {
        rowData.forEach((cell, columnIndex) => {
          const { attributes } = cell;
          if (attributes && attributes.id === clickedActiveComponentId) {
            activeCell = [rowIndex, columnIndex];
          }
        });
      }
    });
  }

  const isTableCellActive =
    Boolean(activeCell) || clickedActiveComponentId === props.section.id;
  const isTableEditable = tableFor === "DOCUMENT_SECTION";
  return {
    activeCell,
    clickedActiveComponentId,
    preventClickedComponentIdUpdate,
    preventEditorFocusOnDrag,
    isTableCellActive,
    isTableEditable,
  };
};

Table.defaultProps = {
  colResize: true,
};

Table.propTypes = {
  colResize: PropTypes.bool,
};

const mapDispatchToProps = {
  updateDocumentClickedComponentID,
  nukeEditorState,
  preventEditorToDeactivate,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Table);



// WEBPACK FOOTER //
// ./src/components/RevvTable/Table.js